import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Spin,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReactToPrint } from 'react-to-print';

import {
  createNewPrompt,
  deleteLLMresponse,
  deleteGroupLLMresponse,
  deletePrompt,
  fetchAdminAIPlayers,
  fetchAdminAISessionContext,
  fetchGroupPrompts,
  fetchGroupResponses,
  fetchAdminAISessions,
  fetchPrompts,
  fetchResponses,
  generateGroupLLMresponse,
  generateLLMresponse,
  selectors,
  updatePrompt,
} from 'state';

import NativeAIRunner from './NativeAIRunner';
import { nativeRequest } from '../../NativeSession/transport';

const PLACEHOLDER_PREFIX = 'Response started generating at UTC';
const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic (Claude Opus 4.7)' },
  { value: 'openai', label: 'OpenAI (GPT-5.5 Pro)' },
];
const PROVIDER_LABELS: { [key: string]: string } = {
  openai: 'OpenAI (GPT-5.5 Pro)',
  anthropic: 'Anthropic (Claude Opus 4.7)',
};

const AdminAI: React.FC = () => {
  const dispatch = useDispatch();

  const prompts = useSelector(selectors.prompts);
  const groupPrompts = useSelector(selectors.groupPrompts);
  const promptsWaiting = useSelector(selectors.promptsWaiting);
  const groupPromptsWaiting = useSelector(selectors.groupPromptsWaiting);
  const llmResponses = useSelector(selectors.llmResponses);
  const llmGroupResponses = useSelector(selectors.llmGroupResponses);
  const llmResponsesWaiting = useSelector(selectors.llmResponsesWaiting);
  // Batch report generation in flight (admin Generate-button lock). True from
  // the request until the report_generation_finished websocket bracket clears it.
  const reportGenerating = useSelector(selectors.reportGenerating);
  const llmGroupResponsesWaiting = useSelector(
    selectors.llmGroupResponsesWaiting
  );
  const sessionOptions = useSelector(selectors.adminAISessions);
  const sessionsWaiting = useSelector(selectors.adminAISessionsWaiting);
  const playerOptions = useSelector(selectors.adminAIPlayers);
  const playersWaiting = useSelector(selectors.adminAIPlayersWaiting);
  const sessionContext = useSelector(selectors.adminAISessionContext);
  const sessionContextWaiting = useSelector(
    selectors.adminAISessionContextWaiting
  );
  const sessionContextError = useSelector(selectors.adminAISessionContextError);

  const [activePromptType, setActivePromptType] = useState<
    'individual' | 'group'
  >('individual');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [promptNameDraft, setPromptNameDraft] = useState('');
  const [promptTextDraft, setPromptTextDraft] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    null
  );
  const [ownership, setOwnership] = useState<{ id: number; engine?: string; error?: string } | null>(null);
  const engine = ownership?.id === selectedSessionId ? ownership.engine : undefined;
  useEffect(() => {
    if (!selectedSessionId) { setOwnership(null); return; }
    const id = selectedSessionId;
    const controller = new AbortController();
    setOwnership(null);
    void nativeRequest<{ execution_engine: string }>(`/sessions/${id}/execution-context/`, undefined, controller.signal)
      .then(value => { if (!controller.signal.aborted) setOwnership({ id, engine: value?.execution_engine }); })
      .catch(reason => { if (!controller.signal.aborted) setOwnership({ id, error: String(reason) }); });
    return () => controller.abort();
  }, [selectedSessionId]);

  // Individual mode now supports multiple players (one request → one batch task).
  const [individualPlayerIds, setIndividualPlayerIds] = useState<number[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([]);
  // The report preview (and its single-player affordances: delete, download,
  // provider header) are only meaningful for ONE player — derive that id; null
  // when zero or several are selected, which automatically cuts the preview.
  const selectedPlayerId =
    individualPlayerIds.length === 1 ? individualPlayerIds[0] : null;
  const [groupReportName, setGroupReportName] = useState('');
  const [selectedGroupResponseId, setSelectedGroupResponseId] = useState<
    number | null
  >(null);
  const [selectedProvider, setSelectedProvider] = useState('anthropic');
  const [awaitingGeneration, setAwaitingGeneration] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [shouldFocusDrafts, setShouldFocusDrafts] = useState(false);
  const [pendingCreatedPrompt, setPendingCreatedPrompt] = useState<{
    name: string;
    minId: number;
    promptType: 'individual' | 'group';
  } | null>(null);

  const nameInputRef = useRef<any>(null);
  const printContentRef = useRef<HTMLDivElement | null>(null);

  const activePrompts = activePromptType === 'group' ? groupPrompts : prompts;
  const activePromptsWaiting =
    activePromptType === 'group' ? groupPromptsWaiting : promptsWaiting;
  const activeResponsesWaiting =
    activePromptType === 'group'
      ? llmGroupResponsesWaiting
      : llmResponsesWaiting;

  const promptList = useMemo(
    () => Object.values(activePrompts).sort((a, b) => b.id - a.id),
    [activePrompts]
  );
  const selectedPrompt =
    selectedPromptId !== null ? activePrompts[selectedPromptId] : undefined;

  const sessionList = useMemo(
    () => sessionOptions.slice().sort((a, b) => b.id - a.id),
    [sessionOptions]
  );
  const playersList = useMemo(
    () =>
      selectedSessionId
        ? playerOptions
            .slice()
            .sort((a, b) => a.username.localeCompare(b.username))
        : [],
    [playerOptions, selectedSessionId]
  );

  const matchingIndividualResponse =
    selectedPromptId !== null && selectedPlayerId !== null
      ? Object.values(llmResponses)
          .filter(
            response =>
              response.prompt === selectedPromptId &&
              response.player === selectedPlayerId
          )
          .sort((a, b) => b.id - a.id)[0]
      : undefined;
  // Players that already have a COMPLETED individual report for the selected
  // prompt (non-placeholder response) — drives the ✓ cue. Updated live by the
  // llm_response_generated socket listener.
  const aiDonePlayerIds = useMemo(
    () =>
      new Set(
        Object.values(llmResponses)
          .filter(
            r =>
              r.prompt === selectedPromptId &&
              !!r.response &&
              !r.response.startsWith(PLACEHOLDER_PREFIX),
          )
          .map(r => r.player),
      ),
    [llmResponses, selectedPromptId],
  );
  // Provider shared by ALL selected players' existing (completed) reports for
  // this prompt — null if they differ or any selected player has no report yet.
  // Drives the provider-dropdown sync: covers single-select (its own provider)
  // and multi-select where every report used the same provider.
  const commonResponseProvider = useMemo(() => {
    if (selectedPromptId === null || !individualPlayerIds.length) return null;
    const providers = new Set<string>();
    for (const pid of individualPlayerIds) {
      const r = Object.values(llmResponses).find(
        x =>
          x.prompt === selectedPromptId &&
          x.player === pid &&
          !!x.response &&
          !x.response.startsWith(PLACEHOLDER_PREFIX),
      );
      if (!r) return null;
      providers.add(r.provider || '');
    }
    return providers.size === 1 ? [...providers][0] || null : null;
  }, [llmResponses, selectedPromptId, individualPlayerIds]);

  const normalizedGroupReportName = groupReportName.trim();
  const matchingGroupResponse =
    selectedPromptId !== null &&
    selectedSessionId !== null &&
    normalizedGroupReportName
      ? Object.values(llmGroupResponses)
          .filter(
            response =>
              response.prompt === selectedPromptId &&
              response.session === selectedSessionId &&
              response.group_report_name === normalizedGroupReportName
          )
          .sort((a, b) => b.id - a.id)[0]
      : undefined;
  const matchingResponse =
    activePromptType === 'group'
      ? matchingGroupResponse
      : matchingIndividualResponse;
  const currentSessionContext =
    sessionContext?.session_id === selectedSessionId ? sessionContext : null;
  const injectedContextText =
    matchingResponse?.injected_context_text ||
    currentSessionContext?.rendered_text ||
    '';
  const showingResponseContextSnapshot =
    !!matchingResponse?.injected_context_text;
  const canDeleteResponse =
    !!selectedPrompt &&
    !!selectedSessionId &&
    (activePromptType === 'group'
      ? !!normalizedGroupReportName
      : !!selectedPlayerId) &&
    !!matchingResponse &&
    !matchingResponse.response.startsWith(PLACEHOLDER_PREFIX);

  const existingGroupReportNames = useMemo(() => {
    if (selectedPromptId === null || selectedSessionId === null) {
      return [];
    }

    return Object.values(llmGroupResponses)
      .filter(
        response =>
          response.prompt === selectedPromptId &&
          response.session === selectedSessionId
      )
      .sort((a, b) => b.id - a.id)
      .map(response => response.group_report_name)
      .filter((value, index, array) => array.indexOf(value) === index);
  }, [llmGroupResponses, selectedPromptId, selectedSessionId]);

  const sessionGroupReports = useMemo(() => {
    if (selectedSessionId === null) {
      return [];
    }

    return Object.values(llmGroupResponses)
      .filter(response => response.session === selectedSessionId)
      .sort((a, b) => b.id - a.id);
  }, [llmGroupResponses, selectedSessionId]);

  const matchingGroupResponsePlayerNames = useMemo(() => {
    if (!matchingGroupResponse) {
      return [];
    }

    return playersList
      .filter(player => matchingGroupResponse.player_ids.includes(player.id))
      .map(player => player.username);
  }, [matchingGroupResponse, playersList]);

  const handlePrint = useReactToPrint({
    content: () => printContentRef.current,
    documentTitle:
      selectedPrompt &&
      activePromptType === 'group' &&
      normalizedGroupReportName
        ? `${selectedPrompt.name} - ${normalizedGroupReportName}`
        : selectedPrompt && selectedPlayerId
        ? `${selectedPrompt.name} - player ${selectedPlayerId}`
        : 'ai-report',
    pageStyle: `
      @page {
        size: auto;
        margin: 16mm;
      }

      body {
        -webkit-print-color-adjust: exact;
      }
    `,
  });

  const hasUnsavedChanges =
    selectedPrompt !== undefined &&
    (promptNameDraft !== selectedPrompt.name ||
      promptTextDraft !== selectedPrompt.text);

  useEffect(() => {
    if (activePromptType === 'group') {
      dispatch(fetchGroupPrompts({ type: 'group' }));
      return;
    }

    dispatch(fetchPrompts({ type: 'individual' }));
  }, [activePromptType, dispatch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      dispatch(fetchAdminAISessions(sessionSearch ? { q: sessionSearch } : {}));
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [dispatch, sessionSearch]);

  useEffect(() => {
    if (pendingCreatedPrompt) {
      if (pendingCreatedPrompt.promptType !== activePromptType) {
        return;
      }

      const createdPrompt = promptList.find(
        prompt =>
          prompt.name === pendingCreatedPrompt.name &&
          prompt.id > pendingCreatedPrompt.minId
      );

      if (createdPrompt) {
        setSelectedPromptId(createdPrompt.id);
        setPendingCreatedPrompt(null);
        return;
      }
    }

    if (promptList.length === 0) {
      setSelectedPromptId(null);
      setPromptNameDraft('');
      setPromptTextDraft('');
      return;
    }

    if (
      selectedPromptId === null ||
      !promptList.some(prompt => prompt.id === selectedPromptId)
    ) {
      setSelectedPromptId(promptList[0].id);
    }
  }, [pendingCreatedPrompt, promptList, selectedPromptId]);

  useEffect(() => {
    if (selectedPrompt) {
      setPromptNameDraft(selectedPrompt.name);
      setPromptTextDraft(selectedPrompt.text);
    }
  }, [selectedPrompt?.id, selectedPrompt?.name, selectedPrompt?.text]);

  useEffect(() => {
    if (!selectedSessionId || engine !== 'legacy') {
      return;
    }

    dispatch(fetchAdminAISessionContext({ session_id: selectedSessionId }));
    dispatch(fetchAdminAIPlayers({ session_id: selectedSessionId }));
    if (activePromptType === 'group') {
      dispatch(fetchGroupResponses({ session_id: selectedSessionId }));
      return;
    }
    dispatch(fetchResponses({ session_id: selectedSessionId }));
  }, [activePromptType, dispatch, selectedSessionId, engine]);

  useEffect(() => {
    setSelectedPromptId(null);
    setIndividualPlayerIds([]);
    setSelectedPlayerIds([]);
    setGroupReportName('');
    setSelectedGroupResponseId(null);
    setAwaitingGeneration(false);
  }, [activePromptType]);

  useEffect(() => {
    if (activePromptType !== 'group') {
      return;
    }

    if (!normalizedGroupReportName) {
      setSelectedGroupResponseId(null);
      return;
    }

    setSelectedGroupResponseId(matchingGroupResponse?.id || null);
  }, [activePromptType, matchingGroupResponse?.id, normalizedGroupReportName]);

  useEffect(() => {
    if (shouldFocusDrafts) {
      window.setTimeout(() => {
        nameInputRef.current?.focus?.();
      }, 0);
      setShouldFocusDrafts(false);
    }
  }, [shouldFocusDrafts, selectedPromptId]);

  useEffect(() => {
    if (!awaitingGeneration) {
      return;
    }

    if (!matchingResponse) {
      return;
    }

    if (!matchingResponse.response.startsWith(PLACEHOLDER_PREFIX)) {
      setAwaitingGeneration(false);
    }
  }, [awaitingGeneration, matchingResponse]);

  useEffect(() => {
    // Group → the matching group report's provider. Individual → the provider
    // common to all selected players' reports (single player: its own provider;
    // several with one provider: that one; mixed / missing: default anthropic).
    const provider =
      activePromptType === 'group'
        ? matchingGroupResponse?.provider
        : commonResponseProvider;
    setSelectedProvider(provider || 'anthropic');
  }, [activePromptType, commonResponseProvider, matchingGroupResponse?.provider]);

  const createPrompt = () => {
    const trimmedName = promptNameDraft.trim();
    const nextName = trimmedName || `Untitled Prompt ${Date.now()}`;
    const nextText = promptTextDraft;

    setAwaitingGeneration(false);
    setPendingCreatedPrompt({
      name: nextName,
      minId: promptList[0]?.id || 0,
      promptType: activePromptType,
    });
    setShouldFocusDrafts(!trimmedName && !nextText.trim());
    dispatch(
      createNewPrompt({
        name: nextName,
        text: nextText,
        type: activePromptType,
      })
    );
  };

  const savePromptDrafts = () => {
    if (!selectedPromptId) {
      return;
    }

    dispatch(
      updatePrompt({
        prompt_id: selectedPromptId,
        name: promptNameDraft,
        text: promptTextDraft,
        type: activePromptType,
      })
    );
  };

  const deleteSelectedPrompt = () => {
    if (!selectedPromptId) {
      return;
    }

    setSelectedPromptId(null);
    setAwaitingGeneration(false);
    dispatch(
      deletePrompt({ prompt_id: selectedPromptId, type: activePromptType })
    );
  };

  const runPrompt = () => {
    if (!selectedPrompt || !selectedSessionId) {
      return;
    }

    if (activePromptType === 'group') {
      setAwaitingGeneration(true);
      dispatch(
        generateGroupLLMresponse({
          prompt_text: selectedPrompt.text,
          prompt_id: selectedPrompt.id,
          session_id: selectedSessionId,
          player_ids: selectedPlayerIds,
          group_report_name: normalizedGroupReportName,
          provider: selectedProvider,
        })
      );
      return;
    }

    if (!individualPlayerIds.length) {
      return;
    }

    // The single-player "generating…" preview state only makes sense for one
    // player; multi-player runs are reflected by the global reportGenerating
    // button lock (so awaitingGeneration doesn't get stuck with no preview).
    if (individualPlayerIds.length === 1) {
      setAwaitingGeneration(true);
    }
    dispatch(
      generateLLMresponse({
        prompt_text: selectedPrompt.text,
        prompt_id: selectedPrompt.id,
        session_id: selectedSessionId,
        player_ids: individualPlayerIds,
        provider: selectedProvider,
      })
    );
  };

  const deleteSelectedResponse = () => {
    if (!selectedPrompt) {
      return;
    }

    setAwaitingGeneration(false);
    if (activePromptType === 'group') {
      if (!matchingGroupResponse) {
        return;
      }

      dispatch(deleteGroupLLMresponse({ id: matchingGroupResponse.id }));
      return;
    }

    if (!selectedPlayerId) {
      return;
    }

    dispatch(
      deleteLLMresponse({
        prompt_id: selectedPrompt.id,
        player_id: selectedPlayerId,
      })
    );
  };

  return (
    <Row gutter={24}>
      <Col span={10}>
        <Card title="Prompt Management">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space>
              <Button
                type={activePromptType === 'individual' ? 'primary' : 'default'}
                onClick={() => setActivePromptType('individual')}
              >
                Individual
              </Button>
              <Button
                type={activePromptType === 'group' ? 'primary' : 'default'}
                onClick={() => setActivePromptType('group')}
              >
                Group
              </Button>
            </Space>

            <div>
              <Typography.Text strong>Select Prompt</Typography.Text>
              <Select<number>
                showSearch
                allowClear
                optionFilterProp="children"
                style={{ width: '100%', marginTop: 8 }}
                placeholder={
                  activePromptsWaiting ? 'Loading prompts...' : 'Select'
                }
                value={selectedPromptId ?? undefined}
                onChange={value => {
                  setSelectedPromptId(value ?? null);
                  setAwaitingGeneration(false);
                }}
              >
                {promptList.map(prompt => (
                  <Select.Option key={prompt.id} value={prompt.id}>
                    {prompt.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              <Typography.Text strong>Prompt Name</Typography.Text>
              <Input
                ref={nameInputRef}
                style={{ marginTop: 8 }}
                value={promptNameDraft}
                onChange={e => setPromptNameDraft(e.target.value)}
                placeholder="Prompt name"
              />
            </div>

            <div>
              <Typography.Text strong>Prompt Text</Typography.Text>
              <TextArea
                style={{ marginTop: 8, minHeight: 380 }}
                value={promptTextDraft}
                onChange={e => setPromptTextDraft(e.target.value)}
                placeholder="Prompt body"
              />
            </div>

            <Space>
              <Button type="primary" onClick={createPrompt}>
                Create
              </Button>
              <Button
                type="primary"
                onClick={savePromptDrafts}
                disabled={
                  !selectedPromptId ||
                  !promptNameDraft.trim() ||
                  !hasUnsavedChanges
                }
                loading={activePromptsWaiting}
              >
                Save
              </Button>
              <Popconfirm
                title="Delete this prompt?"
                onConfirm={deleteSelectedPrompt}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button
                  danger
                  disabled={!selectedPromptId || activePromptsWaiting}
                >
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </Space>
        </Card>
      </Col>

      <Col span={14} style={{ display: 'flex' }}>
        <Card
          title="Prompt Runner"
          style={{
            width: '100%',
            height: 'calc(100vh - 180px)',
            display: 'flex',
            flexDirection: 'column',
          }}
          styles={{
            body: {
              flex: 1,
              minHeight: 0,
              paddingBottom: 24,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <div
            style={{
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div>
              <Typography.Text strong>Session</Typography.Text>
              <Select<number>
                showSearch
                allowClear
                filterOption={false}
                style={{ width: '100%', marginTop: 8 }}
                placeholder={
                  sessionsWaiting ? 'Searching sessions...' : 'Search sessions'
                }
                value={selectedSessionId ?? undefined}
                onSearch={value => setSessionSearch(value)}
                onChange={value => {
                  setSelectedSessionId(value ?? null);
                  setIndividualPlayerIds([]);
                  setSelectedPlayerIds([]);
                  setGroupReportName('');
                  setSelectedGroupResponseId(null);
                  setAwaitingGeneration(false);
                }}
                notFoundContent={sessionsWaiting ? 'Loading...' : 'No sessions'}
              >
                {sessionList.map(session => (
                  <Select.Option key={session.id} value={session.id}>
                    {session.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {selectedSessionId && engine === 'fast' ? (
              <NativeAIRunner key={selectedSessionId} sessionId={selectedSessionId}
                sessionLabel={sessionList.find(s => s.id === selectedSessionId)?.label || String(selectedSessionId)}
                kind={activePromptType} prompt={selectedPrompt} unsaved={hasUnsavedChanges}
                onPrompt={setSelectedPromptId} />
            ) : selectedSessionId && engine !== 'legacy' ? (
              ownership?.id === selectedSessionId ? <Alert type="error" title={ownership.error || 'Unknown execution engine'} /> : <Spin />
            ) : <>
            <div>
              <Typography.Text strong>
                Automatic Session Context
              </Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ marginTop: 4, marginBottom: 8 }}
              >
                {showingResponseContextSnapshot
                  ? 'Snapshot used for the selected report.'
                  : 'Server-generated context that will be sent with the prompt.'}{' '}
                Put {'{{SESSION_CONTEXT}}'} in Prompt Text to control its
                position; otherwise it is appended automatically.
              </Typography.Paragraph>
              <TextArea
                readOnly
                autoSize={{ minRows: 4, maxRows: 10 }}
                value={
                  injectedContextText ||
                  (sessionContextWaiting
                    ? 'Loading automatic session context...'
                    : selectedSessionId
                      ? 'Automatic session context is unavailable.'
                      : 'Select a session to preview its automatic context.')
                }
              />
              {sessionContextError && selectedSessionId ? (
                <Typography.Text type="danger">
                  Automatic session context could not be loaded.
                </Typography.Text>
              ) : null}
            </div>

            {activePromptType === 'group' ? (
              <>
                <div>
                  <Typography.Text strong>Reports</Typography.Text>
                  <Select<number>
                    allowClear
                    disabled={!selectedSessionId || !selectedPromptId}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder={
                      !selectedSessionId || !selectedPromptId
                        ? 'Select a session and prompt first'
                        : sessionGroupReports.length > 0
                        ? 'Select an existing report'
                        : 'No existing reports'
                    }
                    value={selectedGroupResponseId ?? undefined}
                    onChange={value => {
                      const nextId = value ?? null;
                      setSelectedGroupResponseId(nextId);
                      setAwaitingGeneration(false);

                      if (nextId == null) {
                        return;
                      }

                      const selectedExistingReport = sessionGroupReports.find(
                        response => response.id === nextId
                      );
                      if (!selectedExistingReport) {
                        return;
                      }

                      setSelectedPromptId(selectedExistingReport.prompt);
                      setGroupReportName(
                        selectedExistingReport.group_report_name
                      );
                      setSelectedPlayerIds(selectedExistingReport.player_ids);
                    }}
                  >
                    {sessionGroupReports.map(response => (
                      <Select.Option key={response.id} value={response.id}>
                        {response.group_report_name}
                        {' | '}
                        {groupPrompts[response.prompt]?.name ||
                          'Unknown prompt'}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Typography.Text strong>Players</Typography.Text>
                  <Select<number[]>
                    mode="multiple"
                    showSearch
                    optionFilterProp="children"
                    disabled={!selectedSessionId}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder={
                      selectedSessionId
                        ? playersWaiting
                          ? 'Loading players...'
                          : 'Select players'
                        : 'Select a session first'
                    }
                    value={selectedPlayerIds}
                    onChange={value => {
                      setSelectedPlayerIds(value as number[]);
                      setSelectedGroupResponseId(null);
                      setAwaitingGeneration(false);
                    }}
                  >
                    {playersList.map(player => (
                      <Select.Option key={player.id} value={player.id}>
                        {player.username}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Typography.Text strong>Group Report Name</Typography.Text>
                  <Input
                    style={{ marginTop: 8 }}
                    value={groupReportName}
                    onChange={e => {
                      setGroupReportName(e.target.value);
                      setSelectedGroupResponseId(null);
                      setAwaitingGeneration(false);
                    }}
                    placeholder="Enter a new report name or pick an existing one above"
                  />
                  {existingGroupReportNames.length > 0 && (
                    <Typography.Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 8 }}
                    >
                      Existing names: {existingGroupReportNames.join(', ')}
                    </Typography.Text>
                  )}
                </div>
              </>
            ) : (
              <div>
                <Typography.Text strong>
                  Player(s)
                  {individualPlayerIds.length > 1
                    ? ' — preview hidden for multiple players'
                    : ''}
                </Typography.Text>
                <Select<number[]>
                  mode="multiple"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  disabled={!selectedSessionId}
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder={
                    selectedSessionId
                      ? playersWaiting
                        ? 'Loading players...'
                        : 'Select player(s)'
                      : 'Select a session first'
                  }
                  value={individualPlayerIds}
                  onChange={value => {
                    setIndividualPlayerIds((value as number[]) ?? []);
                    setAwaitingGeneration(false);
                  }}
                >
                  {playersList.map(player => (
                    <Select.Option key={player.id} value={player.id}>
                      {aiDonePlayerIds.has(player.id)
                        ? `✓ ${player.username}`
                        : player.username}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <Typography.Text strong>Provider</Typography.Text>
              <Select<string>
                style={{ width: '100%', marginTop: 8 }}
                value={selectedProvider}
                onChange={value => setSelectedProvider(value)}
              >
                {PROVIDER_OPTIONS.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <Space>
              <Button
                type="primary"
                onClick={runPrompt}
                disabled={
                  !selectedPrompt ||
                  !selectedSessionId ||
                  (activePromptType === 'group'
                    ? selectedPlayerIds.length < 2 || !normalizedGroupReportName
                    : !individualPlayerIds.length) ||
                  hasUnsavedChanges ||
                  sessionContextWaiting ||
                  !currentSessionContext ||
                  awaitingGeneration ||
                  reportGenerating
                }
                loading={awaitingGeneration || reportGenerating}
              >
                {reportGenerating ? 'Generating…' : 'Generate'}
              </Button>
              <Button
                onClick={handlePrint}
                disabled={!matchingResponse}
                icon={<DownloadOutlined />}
              >
                Download PDF
              </Button>
              <Popconfirm
                title="Delete this AI response?"
                onConfirm={deleteSelectedResponse}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button danger disabled={!canDeleteResponse}>
                  Delete Response
                </Button>
              </Popconfirm>
              {hasUnsavedChanges && (
                <Typography.Text type="warning">
                  Save prompt changes before generating.
                </Typography.Text>
              )}
              {matchingResponse &&
                matchingResponse.response.startsWith(PLACEHOLDER_PREFIX) && (
                  <Typography.Text type="warning">
                    Wait for generation to finish before deleting.
                  </Typography.Text>
                )}
            </Space>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: 16,
                background: '#fff',
                overflowY: 'auto',
              }}
            >
              {matchingResponse ? (
                <div ref={printContentRef}>
                  <Typography.Title level={3}>
                    {selectedPrompt?.name || 'AI Report'}
                  </Typography.Title>
                  <Typography.Paragraph type="secondary">
                    Session:{' '}
                    {sessionList.find(s => s.id === selectedSessionId)?.label ||
                      '-'}
                  </Typography.Paragraph>
                  {activePromptType === 'group' ? (
                    <>
                      <Typography.Paragraph type="secondary">
                        Group report name:{' '}
                        {matchingGroupResponse?.group_report_name || '-'}
                      </Typography.Paragraph>
                      <Typography.Paragraph type="secondary">
                        Players:{' '}
                        {matchingGroupResponsePlayerNames.join(', ') || '-'}
                      </Typography.Paragraph>
                    </>
                  ) : (
                    <Typography.Paragraph type="secondary">
                      Player:{' '}
                      {playersList.find(
                        player => player.id === selectedPlayerId
                      )?.username || '-'}
                    </Typography.Paragraph>
                  )}
                  <Typography.Paragraph type="secondary">
                    Provider:{' '}
                    {PROVIDER_LABELS[matchingResponse.provider || 'openai'] ||
                      matchingResponse.provider ||
                      '-'}
                  </Typography.Paragraph>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {matchingResponse.response}
                  </ReactMarkdown>
                </div>
              ) : (
                <Empty
                  description={
                    activePromptType === 'group'
                      ? 'Select a prompt, session, players, and group report name to view or run a report'
                      : individualPlayerIds.length > 1
                        ? 'Preview hidden — multiple players selected. Generate runs a report for each; select a single player to preview one.'
                        : 'Select a prompt, session, and player to view or run a report'
                  }
                />
              )}
            </div>
            </>}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default AdminAI;
